/**
 * Where-are-you / what-do-they-speak-there lookup tables for the dashboard greeting.
 *
 * We have no country column on the user, so the greeting infers place from the browser's IANA
 * time zone (`Intl.DateTimeFormat().resolvedOptions().timeZone`) and falls back to the region
 * subtag of the browser locale. Both are client-side and free.
 *
 * The zone table only lists zones whose country maps to a language we actually greet in, plus
 * the busy English-speaking zones. An unlisted zone just falls through to the browser locale
 * and, ultimately, to English — a miss costs nothing.
 */

/** IANA time zone -> ISO-3166 alpha-2 country. */
export const TIMEZONE_COUNTRY: Record<string, string> = {
  // North America
  'America/Adak': 'US',
  'America/Anchorage': 'US',
  'America/Boise': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Juneau': 'US',
  'America/Kentucky/Louisville': 'US',
  'America/Los_Angeles': 'US',
  'America/New_York': 'US',
  'America/Phoenix': 'US',
  'America/Puerto_Rico': 'US',
  'Pacific/Honolulu': 'US',
  'America/Edmonton': 'CA',
  'America/Halifax': 'CA',
  'America/Moncton': 'CA',
  'America/Montreal': 'CA',
  'America/Regina': 'CA',
  'America/St_Johns': 'CA',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Winnipeg': 'CA',
  'America/Cancun': 'MX',
  'America/Chihuahua': 'MX',
  'America/Mazatlan': 'MX',
  'America/Merida': 'MX',
  'America/Mexico_City': 'MX',
  'America/Monterrey': 'MX',
  'America/Tijuana': 'MX',

  // Central & South America
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Mendoza': 'AR',
  'America/Asuncion': 'PY',
  'America/Bogota': 'CO',
  'America/Caracas': 'VE',
  'America/Costa_Rica': 'CR',
  'America/El_Salvador': 'SV',
  'America/Guatemala': 'GT',
  'America/Guayaquil': 'EC',
  'America/Havana': 'CU',
  'America/La_Paz': 'BO',
  'America/Lima': 'PE',
  'America/Managua': 'NI',
  'America/Montevideo': 'UY',
  'America/Panama': 'PA',
  'America/Santiago': 'CL',
  'America/Santo_Domingo': 'DO',
  'America/Bahia': 'BR',
  'America/Belem': 'BR',
  'America/Fortaleza': 'BR',
  'America/Manaus': 'BR',
  'America/Recife': 'BR',
  'America/Sao_Paulo': 'BR',

  // Western Europe
  'Atlantic/Reykjavik': 'IS',
  'Europe/Amsterdam': 'NL',
  'Europe/Andorra': 'AD',
  'Europe/Berlin': 'DE',
  'Europe/Brussels': 'BE',
  'Europe/Copenhagen': 'DK',
  'Europe/Dublin': 'IE',
  'Europe/Gibraltar': 'GB',
  'Europe/Helsinki': 'FI',
  'Europe/Lisbon': 'PT',
  'Atlantic/Azores': 'PT',
  'Atlantic/Madeira': 'PT',
  'Europe/London': 'GB',
  'Europe/Luxembourg': 'LU',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'Europe/Malta': 'MT',
  'Europe/Monaco': 'FR',
  'Europe/Oslo': 'NO',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/Stockholm': 'SE',
  'Europe/Vaduz': 'LI',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',

  // Central & Eastern Europe
  'Europe/Athens': 'GR',
  'Europe/Belgrade': 'RS',
  'Europe/Bratislava': 'SK',
  'Europe/Bucharest': 'RO',
  'Europe/Budapest': 'HU',
  'Europe/Chisinau': 'MD',
  'Europe/Kaliningrad': 'RU',
  'Europe/Kiev': 'UA',
  'Europe/Kyiv': 'UA',
  'Europe/Ljubljana': 'SI',
  'Europe/Minsk': 'BY',
  'Europe/Moscow': 'RU',
  'Europe/Nicosia': 'CY',
  'Europe/Prague': 'CZ',
  'Europe/Riga': 'LV',
  'Europe/Samara': 'RU',
  'Europe/Sarajevo': 'BA',
  'Europe/Sofia': 'BG',
  'Europe/Tallinn': 'EE',
  'Europe/Vilnius': 'LT',
  'Europe/Warsaw': 'PL',
  'Europe/Zagreb': 'HR',
  'Asia/Yekaterinburg': 'RU',
  'Asia/Novosibirsk': 'RU',
  'Asia/Krasnoyarsk': 'RU',
  'Asia/Irkutsk': 'RU',
  'Asia/Vladivostok': 'RU',

  // Middle East & Africa
  'Africa/Cairo': 'EG',
  'Africa/Casablanca': 'MA',
  'Africa/Johannesburg': 'ZA',
  'Africa/Lagos': 'NG',
  'Africa/Nairobi': 'KE',
  'Africa/Tunis': 'TN',
  'Africa/Algiers': 'DZ',
  'Asia/Amman': 'JO',
  'Asia/Baghdad': 'IQ',
  'Asia/Beirut': 'LB',
  'Asia/Dubai': 'AE',
  'Asia/Jerusalem': 'IL',
  'Asia/Tel_Aviv': 'IL',
  'Asia/Kuwait': 'KW',
  'Asia/Qatar': 'QA',
  'Asia/Riyadh': 'SA',
  'Asia/Istanbul': 'TR',
  'Europe/Istanbul': 'TR',

  // Asia & Pacific
  'Asia/Bangkok': 'TH',
  'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Saigon': 'VN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Jakarta': 'ID',
  'Asia/Makassar': 'ID',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Manila': 'PH',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN',
  'Asia/Chongqing': 'CN',
  'Asia/Singapore': 'SG',
  'Asia/Taipei': 'TW',
  'Asia/Tokyo': 'JP',
  'Australia/Adelaide': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Darwin': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Sydney': 'AU',
  'Pacific/Auckland': 'NZ',
  'Pacific/Fiji': 'FJ',
};

/**
 * Country -> languages we might greet in, most likely first.
 *
 * Where a country has more than one entry it is genuinely multilingual, and the browser's
 * language list breaks the tie (a Geneva laptop set to French gets "Bonjour", one set to
 * German gets "Grüezi"). With no usable hint we take the first entry.
 *
 * Countries whose language we don't have a greeting table for are simply absent, which lands
 * the greeting on English.
 */
export const COUNTRY_LANGUAGES: Record<string, string[]> = {
  AD: ['ca', 'es'],
  AE: ['ar', 'en'],
  AR: ['es'],
  AT: ['de'],
  AU: ['en'],
  BA: ['hr', 'sr'],
  BE: ['nl', 'fr'],
  BG: ['bg'],
  BO: ['es'],
  BR: ['pt'],
  BY: ['ru'],
  CA: ['en', 'fr'],
  CH: ['de', 'fr', 'it'],
  CL: ['es'],
  CN: ['zh-Hans'],
  CO: ['es'],
  CR: ['es'],
  CU: ['es'],
  CY: ['el', 'tr'],
  CZ: ['cs'],
  DE: ['de'],
  DK: ['da'],
  DO: ['es'],
  DZ: ['ar', 'fr'],
  EC: ['es'],
  EE: ['et'],
  EG: ['ar'],
  ES: ['es', 'ca'],
  FI: ['fi', 'sv'],
  FJ: ['en'],
  FR: ['fr'],
  GB: ['en'],
  GR: ['el'],
  GT: ['es'],
  HK: ['zh-Hant', 'en'],
  HR: ['hr'],
  HU: ['hu'],
  ID: ['id'],
  IE: ['en'],
  IL: ['he', 'ar'],
  IN: ['hi', 'en'],
  IQ: ['ar'],
  IS: ['is'],
  IT: ['it'],
  JO: ['ar'],
  JP: ['ja'],
  KE: ['en'],
  KR: ['ko'],
  KW: ['ar'],
  LB: ['ar', 'fr'],
  LI: ['de'],
  LT: ['lt'],
  LU: ['fr', 'de'],
  LV: ['lv'],
  MA: ['ar', 'fr'],
  MD: ['ro', 'ru'],
  MT: ['en', 'it'],
  MX: ['es'],
  MY: ['ms', 'en'],
  NG: ['en'],
  NI: ['es'],
  NL: ['nl'],
  NO: ['nb'],
  NZ: ['en'],
  PA: ['es'],
  PE: ['es'],
  PH: ['fil', 'en'],
  PL: ['pl'],
  PT: ['pt'],
  PY: ['es'],
  QA: ['ar'],
  RO: ['ro'],
  RS: ['sr'],
  RU: ['ru'],
  SA: ['ar'],
  SE: ['sv'],
  SG: ['en', 'zh-Hans', 'ms'],
  SI: ['sl'],
  SK: ['sk'],
  SV: ['es'],
  TH: ['th'],
  TN: ['ar', 'fr'],
  TR: ['tr'],
  TW: ['zh-Hant'],
  UA: ['uk'],
  US: ['en'],
  UY: ['es'],
  VE: ['es'],
  VN: ['vi'],
  ZA: ['en', 'af'],
};
